import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Loader from "../../components/Loader";
import { MdDelete, MdDownload, MdEdit, MdArrowBack } from "react-icons/md";
import { Formik, Form } from "formik";
import TextInput from "../../components/TextInput";
import { statusOptions } from "../../Constants";
import CustomDropdown from "../../components/CustomDropdown";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import CustomDataTable from "../../components/CustomDataTable";
import DocumentModal from "./DocumentModal";
import { toast } from "react-toastify";
import { FaRegCalendarAlt } from "react-icons/fa";
import CustomDatePicker from "../../components/CustomDatePicker";
import MuiDatePicker from "../../components/MuiDatePicker";
import CustomToolTip from "../../components/ToolTip";
import { useLocation } from "react-router-dom";

import Hoc from "../../components/HOC";
import { EmployeeContext } from "../../Context/Employee";
import { getEmployeeById } from "../../Services/employee";
import SimpleTable from "../../components/SimpleTable";

const UploadDocuments = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const { fetchEmployeeDocuments, deleteEmployeeDocument ,uploadDocumentMgEmployee} = useContext(EmployeeContext);
  const [documents, setDocuments] = useState([]);
  const [Loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const location = useLocation();
  const { employeeName } = location.state || {};
  const [currentEmployeeName, setCurrentEmployeeName] = useState(employeeName);
  const [documentStatus, setDocumentStatus] = useState("Active");

  // Document types list
  const documentsList = [
    { label: "Passport", value: "PASSPORT" },
    { label: "Visa", value: "VISA" },
    { label: "Driving License", value: "driving_license" },
    { label: "SSN", value: "SSN" },
    { label: "Resume", value: "Resume" },
    { label: "Other", value: "OTHER" }
  ];

  // Initial values for the document form
  const initialDocumentValues = {
    documentType: "",
    file: null,
    issueDate: "",
    expiryDate: "",
    statusCode: "A",
    others: ""
  };

  const fetchEmployeeDocumentsData = async (status) => {
    try {
      setLoading(true);
      const data = await fetchEmployeeDocuments(id, status);
      console.log("Fetched documents:", data);
      // Reverse the order of the fetched data
      const reversedData = [...data].reverse(); // Create a shallow copy before reversing to avoid mutating original array if it's used elsewhere
      setDocuments(reversedData);
      setError(null);
    } catch (err) {
      console.error("Error fetching documents:", err);
      setError("Failed to fetch documents. Please try again.");
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch when component mounts
  useEffect(() => {
    fetchEmployeeDocumentsData(documentStatus);

    if (!currentEmployeeName) {
      const fetchEmployee = async () => {
        try {
          const employee = await getEmployeeById(id);
          if (employee) {
            setCurrentEmployeeName(`${employee.fname || ''} ${employee.lname || ''}`.trim());
          }
        } catch (err) {
          console.error("Failed to fetch employee details:", err);
        }
      };
      fetchEmployee();
    }
  }, [id, documentStatus, currentEmployeeName]);

  const handleCloseAddModal = () => {
    setShowUploadModal(false);
  };

   const handleSubmitDocument = async (values) => {
    try {
      const response = await uploadDocumentMgEmployee(id, values.documentType, values.issueDate, values.expiryDate, values.statusCode, values.file);
      console.log(response);
      toast.success("Document uploaded successfully");
      fetchEmployeeDocumentsData(documentStatus);
    } catch (error) {
      console.error("Full error object in handleSubmitDocument:", error);
      console.error("Error response in handleSubmitDocument:", error.response);
      console.error("Error response data in handleSubmitDocument:", error.response?.data);
      toast.error(error.response?.data?.message || "This file is not accepted. Accepted formats are PDF, PNG, JPG, JPEG, BMP, GIF.");
    }
   }

  // Function to download a document using its downloadLink
  const downloadDocument = (docData) => {
    try {
      if (!docData.downloadLink) {
        toast.error("Download link not available");
        return;
      }

      // Create an anchor element
      const link = window.document.createElement('a');
      
      // Set the download link
      link.href = docData.downloadLink;
      
      // Use originalFilename if available, otherwise fallback to old logic
      let filename = docData.originalFilename;
      if (!filename) {
        const extension = getFileExtension(docData.fileType);
        const documentType = docData.documentType ? docData.documentType.toLowerCase() : 'document';
        filename = `${documentType}.${extension}`;
      }
      link.download = filename;
      
      // Append to the body (for Firefox compatibility)
      window.document.body.appendChild(link);
      
      // Trigger the download
      link.click();
      
      // Clean up
      window.document.body.removeChild(link);
      
      toast.success(`Downloading ${filename}`);
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document");
    }
  };

  // Helper function to get file extension from MIME type
  const getFileExtension = (mimeType) => {
    const extensions = {
      'application/pdf': 'pdf',
      'image/jpeg': 'jpg',
      'image/png': 'png',
      'image/gif': 'gif',
      'application/msword': 'doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
      'application/vnd.ms-excel': 'xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
      'text/plain': 'txt'
    };
    
    return extensions[mimeType] || 'file';
  };

  // Handle document actions (delete, download)
  const handleDocumentAction = async (action, employeeId, docData) => {
    if (action === "delete") {
      const result = await deleteEmployeeDocument(employeeId, docData.documentType, documentStatus);
  
      if (result.success) {
        toast.success(result.message);
        setDocuments(result.data); // update local state if needed
      } else {
        toast.error(result.message);
      }
  
    } else if (action === "download") {
      downloadDocument(docData);
    }
  };
  
  // Define columns for SimpleTable
  const columns = [
    {
      name: 'Document Type',
      selector: 'documentType',
      // Add className if needed, matching the original header style
      // className: 'px-6 py-3 text-left text-xs font-semibold text-[#1e293b] uppercase tracking-wider w-[25%]'
    },
    {
      name: 'Issue Date',
      selector: 'issueDate',
      // Add renderCell to format date if needed, matching the original cell rendering
      renderCell: (row) => row.issueDate ? moment(row.issueDate).format("MM-DD-YYYY") : "Not Applicable"
    },
    {
      name: 'Expiry Date',
      selector: 'expiryDate',
      // Add renderCell to format date if needed, matching the original cell rendering
      renderCell: (row) => row.expiryDate ? moment(row.expiryDate).format("MM-DD-YYYY") : "Not Applicable"
    },
    {
      name: 'Updated By',
      selector: 'updatedBy',
       renderCell: (row) => row.updatedBy || "N/A"
    },
    {
      name: 'Actions',
      selector: 'actions',
      renderCell: (row) => (
        <div className="flex items-center justify-center space-x-4">
          <button
            onClick={() => handleDocumentAction("delete", id, row)}
            className="text-red-500 hover:text-red-700 disabled:cursor-not-allowed p-2 rounded-full hover:bg-red-50 transition-colors"
          >
            <>
              <MdDelete size={20} data-tip data-for="Delete" />
              <CustomToolTip id="Delete" title="Delete" />
            </>
          </button>
          <button
            onClick={() => handleDocumentAction("download", id, row)}
            className=" disabled:cursor-not-allowed p-2 rounded-full  transition-colors hover:bg-gray-200"
          >
            <>
              <MdDownload size={20} data-tip data-for="Download" />
              <CustomToolTip id="Download" title="Download" />
            </>
          </button>
        </div>
      ),
      headerClassName: 'text-center',
    },
  ];

  return (
    <div className="w-full px-4 py-6 flex flex-col">
      <Loader isLoading={Loading} />
  
      <div className="mb-6 w-full rounded-lg border border-gray-200 bg-white px-4 py-4 shadow-sm sm:px-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:items-center sm:gap-4">
          <h2 className="order-1 text-center text-lg font-semibold leading-snug text-[#201F1E] sm:order-none sm:col-start-2 sm:row-start-1 sm:px-2 sm:text-xl min-w-0">
            <span className="block text-xs font-medium uppercase tracking-wide text-[#656464] sm:text-sm">
              Documents
            </span>
            <span className="mt-0.5 block">
              Upload for{" "}
              <span className="break-words font-semibold text-[#201F1E]">{currentEmployeeName}</span>
            </span>
          </h2>
          <div className="order-2 flex items-center justify-between gap-3 sm:contents">
            <button
              type="button"
              onClick={() => navigate("/manage-employees")}
              className="inline-flex h-10 shrink-0 items-center justify-center rounded-[4px] border border-[#D1D1D1] bg-white px-4 text-sm font-medium text-[#201F1E] transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#201F1E]/20 focus:ring-offset-1 sm:col-start-1 sm:row-start-1"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={() => setShowUploadModal(true)}
              className="inline-flex h-10 shrink-0 items-center justify-center rounded-[4px] bg-[#201F1E] px-5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#2b2a29] focus:outline-none focus:ring-2 focus:ring-[#201F1E]/30 focus:ring-offset-1 sm:col-start-3 sm:row-start-1"
            >
              Upload Document
            </button>
          </div>
        </div>
      </div>
      
      {/* Document Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <SimpleTable columns={columns} data={documents} />
      </div>

      {/* Link to View Archived Documents */}
      <div className="flex justify-end mt-4">
        <Link
          to={{
            pathname: `/manage-employees/${id}/documents/archived`,
            state: { employeeName: employeeName }
          }}
          className="text-[#201F1E] hover:text-[#201F1E] font-semibold"
        >
          View Archived Documents
        </Link>
      </div>
  
      {/* Upload Modal */}
      <DocumentModal
        show={showUploadModal}
        onClose={handleCloseAddModal}
        title="Upload Document"
        initialValues={initialDocumentValues}
        documentsList={documentsList}
        submitForm={handleSubmitDocument}
      />
    </div>
  );
};

export default Hoc(UploadDocuments);